import { KeyboardEvent, useRef, useState } from 'react';

import { Box, BoxProps, Col, Image } from '@opendoor/bricks-next';
import { EntryComponent } from 'cms/entries/entries';
import { motion } from 'framer-motion';

import { ILpComponentFeature5050 } from '../../declarations/contentful';
import { LPLink } from './shared/Buttons';
import Container from './shared/Container';
import { show } from './shared/motion/MotionVariants';
import Sheet from './shared/Sheet';
import { getComponentThemeColors, IComponentThemeOptions } from './shared/styles/ComponentTheme';
import { Desc, Eyebrow, Subhead, TitleWithAccent } from './shared/Typography';

const bricksBgToToken: Record<string, BoxProps['backgroundColor']> = {
  white0: '$backgroundPrimary',
  warmgrey100: '$backgroundSecondary',
  earth100: '$backgroundSecondary',
  earth200: '$backgroundTertiary',
  brandBlue600: '$backgroundAccentBrand',
  warmgrey200: '$backgroundTertiary',
  warmgrey800: '$backgroundInversePrimary',
  warmgrey950: '$backgroundInversePrimary',
  neutrals100: '$backgroundInversePrimary',
};

const Feature5050Image = ({
  isArched,
  imageSrc,
  imageAlt,
}: {
  isArched: boolean;
  imageSrc: string;
  imageAlt: string;
}) => {
  return (
    <Box
      display="block"
      overflow="hidden"
      width="100%"
      aspectRatio={1}
      $largerThanSM={{ height: '100%', aspectRatio: 'auto' }}
      style={{
        borderRadius: isArched ? '99em 99em 8em 8em' : '2em',
      }}
    >
      <Image
        source={{ uri: imageSrc }}
        alt={imageAlt}
        width="100%"
        height="100%"
        objectFit="cover"
      />
    </Box>
  );
};

interface CtaSheetProps {
  entry: ILpComponentFeature5050;
  componentTheme: IComponentThemeOptions;
}

const CtaSheet = ({ entry, componentTheme }: CtaSheetProps) => {
  const { fields } = entry;

  const [isVisible, setIsVisible] = useState(false);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  const exitComplete = () => {
    ctaRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      setIsVisible(true);
    }
  };

  return (
    <>
      {fields.ctaDisplayText && (
        <LPLink
          aria-label={fields.ctaSheetLabel || 'Open dialog'}
          analyticsName={`cosmos-landing-page-feature5050-${fields?.analyticsId}`}
          componentTheme={componentTheme}
          ctaStyle={fields.ctaStyle}
          ctaDisplayText={fields.ctaDisplayText}
          mt="$0"
          tabIndex={0}
          onKeyDown={onKeyDown}
          ref={ctaRef}
          onPress={() => setIsVisible(true)}
        >
          {fields.ctaDisplayText}
        </LPLink>
      )}
      <Sheet
        entry={fields.ctaSheet}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        exitComplete={exitComplete}
      />
    </>
  );
};

const RenderFeature5050 = (entry: ILpComponentFeature5050) => {
  const { fields } = entry;
  const componentTheme = getComponentThemeColors(fields.backgroundColor);
  const bgColor = componentTheme.bg ? String(componentTheme.bg) : 'white0';
  const bgToken = bricksBgToToken[bgColor] || '$backgroundPrimary';
  const { isArched = false, imagePosition = 'Right' } = fields;

  const gridLayout: BoxProps['$platform-web'] = { display: 'grid' };

  return (
    <Container>
      <motion.div variants={show} initial="start" animate="end">
        <Box
          display="block"
          backgroundColor={bgToken}
          $largerThanSM={gridLayout as BoxProps['$largerThanSM']}
          $platform-web={{
            gridTemplateColumns: 'repeat(2, 1fr)',
            justifyItems: 'center',
          }}
          borderRadius="$12x"
        >
          <Col
            backgroundColor={bgToken}
            height="100%"
            justifyContent="flex-end"
            pb="$14"
            pt="$16"
            px="$6"
            $largerThanMD={{
              pb: '$27',
              pt: '$29',
            }}
            $largerThanLG={{
              pl: imagePosition === 'Left' ? '$24' : '$16',
              pr: imagePosition === 'Left' ? '$16' : '$24',
            }}
            gap="$6"
            width="100%"
            borderRadius="$12x"
            $platform-web={{
              gridColumn: imagePosition === 'Left' ? '2 / -1' : '1 / 2',
              gridRow: '1 / -1',
              textAlign: 'left',
            }}
          >
            <Col gap="$2">
              {fields.eyebrow && <Eyebrow>{fields.eyebrow}</Eyebrow>}
              {fields.title && (
                <TitleWithAccent title={fields.title} titleAccent={fields.titleAccent} />
              )}
              {fields.subhead && <Subhead>{fields.subhead}</Subhead>}
              {fields.desc && <Desc mt="$4x">{fields.desc}</Desc>}
            </Col>
            {fields.ctaDisplayText && (fields.ctaUrl || fields.ctaSheet) && (
              <Box display="block">
                {fields.ctaDisplayText && fields.ctaUrl && (
                  <LPLink
                    aria-label={`Go to page ${fields?.ctaDisplayText}`}
                    analyticsName={`cosmos-landing-page-feature5050-${fields?.analyticsId}`}
                    componentTheme={componentTheme}
                    ctaStyle={fields.ctaStyle}
                    ctaDisplayText={fields.ctaDisplayText}
                    ctaUrl={fields.ctaUrl}
                    mt="$0"
                  >
                    {fields.ctaDisplayText}
                  </LPLink>
                )}
                {fields.ctaDisplayText && fields.ctaSheet && (
                  <CtaSheet entry={entry} componentTheme={componentTheme} />
                )}
              </Box>
            )}
          </Col>

          <Box
            display="block"
            backgroundColor={bgToken}
            width="100%"
            p="$4"
            borderRadius="$12x"
            $platform-web={{
              gridColumn: imagePosition === 'Left' ? '1 / 2' : '2 / -1',
              gridRow: '1 / -1',
            }}
          >
            {fields.image && (
              <Feature5050Image
                isArched={isArched}
                imageSrc={fields.image.fields?.file?.url}
                imageAlt={fields.image.fields?.description || fields.image.fields?.title}
              />
            )}
          </Box>
        </Box>
      </motion.div>
    </Container>
  );
};

const Feature5050: EntryComponent<ILpComponentFeature5050> = {
  render: RenderFeature5050,
};

export default Feature5050;
